/**
 * The seam between "a request was refused because this account has NO second
 * factor" (`MFA_ENROLLMENT_REQUIRED`) and "send the person to set one up".
 *
 * The sibling of `mfaStepUpBroker`, for the other half of the same gate: a
 * step-up asks someone to USE a factor they hold; this asks someone who holds
 * none to ADD one. Built from the same `createStepUpBroker`, for the same
 * reasons — the refusal arrives in an axios interceptor with no React tree
 * beneath it, and a page that fires six requests at once must open ONE prompt,
 * not six.
 *
 * The interceptor awaits the answer and replays the refused request once when
 * it is true: completing an enrolment grants the step-up, so the replay passes.
 *
 * Mounted by `MfaEnrollmentProvider` in the OrderUp staff consoles (admin,
 * billing, CRM), whose privileged routes refuse an unenrolled staff member
 * under `STAFF_MFA_POLICY=enroll|enforce`. With nothing mounted, `request`
 * resolves false and nothing else changes.
 */
import { notifySecondFactorPresented } from './secondFactorPresented';
import { createStepUpBroker } from './stepUpBroker';

/** Resolves true once the person has enrolled and continued, false if they put it off. */
export type MfaEnrollmentHandler = () => Promise<boolean>;

const broker = createStepUpBroker<[]>('MfaEnrollmentBroker');

/** Register the UI that offers enrolment. Returns an unregister function. */
export function registerMfaEnrollmentHandler(fn: MfaEnrollmentHandler): () => void {
  return broker.register(fn);
}

/** True when a surface can actually offer enrolment. */
export function canPromptForMfaEnrollment(): boolean {
  return broker.canPrompt();
}

/** Offer enrolment, coalescing concurrent refusals into one prompt. Never throws. */
export function requestMfaEnrollment(): Promise<boolean> {
  return broker.request().then((presented) => {
    // The session now holds a step-up: let the console carry it into its
    // Firebase session too (SOC 2 R73, `StaffFirebaseFactorSync`).
    if (presented) notifySecondFactorPresented();
    return presented;
  });
}

/** Test seam. Never call from application code. */
export function __resetMfaEnrollmentBrokerForTests(): void {
  broker.reset();
}
